import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { Outlet, generatePath, useOutletContext } from 'react-router';

import { useTypedParams } from '@sorare/routing';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  LeaguePageTabs,
  Tabs,
} from '@sorare/core/src/components/seo/League/Tabs';
import {
  FOOTBALL_LEAGUES_SLUG,
  FOOTBALL_LEAGUES_SLUG_CARDS,
  FOOTBALL_LEAGUES_SLUG_FIXTURES,
} from '@sorare/core/src/constants/__generated__/routes';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import type {
  FootballSeoLeagueQuery,
  FootballSeoLeagueQueryVariables,
} from './__generated__/layout.graphql.ts';
import type { PageParams } from './__generated__/routeParams.ts';

const FOOTBALL_SEO_LEAGUE_QUERY = gql`
  query FootballSeoLeagueQuery($slug: String!) {
    football {
      competition(slug: $slug) {
        slug
        displayName
        name
      }
    }
  }
` as TypedDocumentNode<FootballSeoLeagueQuery, FootballSeoLeagueQueryVariables>;

type LayoutData = FootballSeoLeagueQuery['football']['competition'];

export const useLayoutData = () => {
  return useOutletContext<LayoutData>();
};

export const loader = queryLoader(
  FOOTBALL_SEO_LEAGUE_QUERY,
  ({ slug }: PageParams) => ({
    slug,
  })
);

const LeagueLayout = () => {
  const { data } = useQueryLoader<FootballSeoLeagueQuery>();

  const { slug } = useTypedParams<PageParams>();
  const { competition } = data.football;
  const tabs: Record<LeaguePageTabs, string> = useMemo(() => {
    return {
      [LeaguePageTabs.OVERVIEW]: generatePath(FOOTBALL_LEAGUES_SLUG, {
        slug,
      }),
      [LeaguePageTabs.FIXTURES]: generatePath(FOOTBALL_LEAGUES_SLUG_FIXTURES, {
        slug,
      }),
      [LeaguePageTabs.CARDS]: generatePath(FOOTBALL_LEAGUES_SLUG_CARDS, {
        slug,
      }),
    };
  }, [slug]);

  if (!competition) return null;

  return (
    <>
      <PageHeader text={competition.displayName} />
      <ContentWithStickyHeader header={<Tabs tabs={tabs} />}>
        <Outlet context={competition satisfies LayoutData} />
      </ContentWithStickyHeader>
    </>
  );
};

export default LeagueLayout;
